import React, { useMemo } from 'react';

import useLoadStoreFromSalesChannelIdIntoRedux from '@fd/customHooks/useLoadStoreFromSalesChannelIdIntoRedux';
import useQueryRMSCommonDataHook from '@fd/customHooks/useQueryRMSCommonDataHook';
import { SalesChannelType } from '@fd/customHooks/useQueryRMSSalesChannels';
import { useQuery } from '@tanstack/react-query';
import { getActiveLanguage } from 'react-localize-redux';
import { connect } from 'react-redux';
import { useParams } from 'react-router';

import ErrorComponent from '@fd/ui/ErrorComponent';
import MicroFrontend from '@fd/ui/MicroFrontend/MicroFrontend';

import { AppLoadingCircularProgress } from '../AppLoadingCircularProgress';
import { getIsFlipdishChannel } from '../RMS/SalesChannels/utils';
import { getPropertyById } from './properties.services';

type RouteParams = { propertyId: string; channelId?: string; type?: string };

type PassedProps = {
  baseUrl: string;
  entityType: string;
};

type AllProps = MappedState & PassedProps;

const PARTIAL_INDEX_URL = {
  prod: 'https://clients.portal-mfs.flipdish.com/property-management/production',
  staging:
    'https://clients-integration.portal-mfs.flipdishdev.com/property-management/prod-staging',
};

const logMissingRmsData = (context: Record<string, unknown>, isFlipdishChannel: boolean): void => {
  const { rmsPropertyId, franchisorId, franchiseeId, storefrontId, finalStoreId, channelId } =
    context;

  const missingProps = [
    !rmsPropertyId && 'rmsPropertyId',
    !franchisorId && 'franchisorId',
    !franchiseeId && 'franchiseeId',
    channelId && !storefrontId && 'storefrontId',
    channelId && isFlipdishChannel && !finalStoreId && 'storeId',
  ].filter(Boolean);

  if (missingProps.length > 0) {
    console.error('[PropertyManagement] Missing RMS data', { missingProps, ...context });
  }
};

const PropertyManagement = ({ currentApp, languageCode, baseUrl, entityType }: AllProps) => {
  const { propertyId, channelId = '' } = useParams<RouteParams>();
  const orgId = currentApp?.OrgId ?? '';
  const appId = currentApp?.AppId ?? '';

  const {
    isLoading: isLoadingRmsData,
    error: rmsDataError,
    data: rmsData,
  } = useQueryRMSCommonDataHook(appId);

  const assocProperties = rmsData?.associations?.orgHierarchy?.properties;

  const rmsPropertyData = useMemo(() => {
    const property = assocProperties?.find((p) => p.code === propertyId);
    const storefront = property?.storefronts?.find((s) => s.code === channelId);

    if (!isLoadingRmsData && !rmsDataError) {
      if (!assocProperties?.length) {
        console.warn('[PropertyManagement] No properties in RMS data', {
          appId,
          orgId,
          propertyId,
        });
      } else if (!property) {
        console.warn('[PropertyManagement] Property not found', {
          propertyId,
          available: assocProperties.map((p) => p.code),
        });
      } else if (channelId && !storefront) {
        console.warn('[PropertyManagement] Storefront not found', {
          propertyId,
          channelId,
          available: property?.storefronts?.map((s) => s.code),
        });
      }
    }

    return {
      rmsPropertyId: property?.id ?? '',
      franchiseeId: property?.franchiseeId ?? '',
      franchisorId: property?.franchisorId ?? '',
      storefrontId: storefront?.id ?? '',
      type: storefront?.type ?? '',
      storeId: storefront?.fieldAssociations?.flipdish?.storeId ?? '',
    };
  }, [assocProperties, propertyId, channelId, isLoadingRmsData, rmsDataError, appId, orgId]);

  const { rmsPropertyId, storefrontId, franchiseeId, franchisorId, storeId, type } =
    rmsPropertyData;

  const isFlipdishChannel = getIsFlipdishChannel(type as SalesChannelType);

  const { storeId: storeIdFromFallback, loadingStoreAssociation } =
    useLoadStoreFromSalesChannelIdIntoRedux({
      linkedSalesChannelId: channelId,
      enabled: !storeId && !!channelId && isFlipdishChannel,
    });

  const finalStoreId = storeId || storeIdFromFallback;

  const {
    data: propertyResponse,
    isLoading: isLoadingPropertyData,
    error: propertyDataError,
  } = useQuery({
    queryKey: ['getPropertyById', orgId, propertyId],
    queryFn: () => getPropertyById(orgId, propertyId),
    enabled: !!propertyId,
  });

  const country = propertyResponse?.data?.data?.countryCode ?? '';

  const partialIndexUrl = window.location?.host?.includes('portal.flipdish.com')
    ? PARTIAL_INDEX_URL.prod
    : PARTIAL_INDEX_URL.staging;

  if (isLoadingRmsData || isLoadingPropertyData || loadingStoreAssociation) {
    return <AppLoadingCircularProgress />;
  }

  if (rmsDataError || propertyDataError) {
    return (
      <div>
        <ErrorComponent title="Something_went_wrong" subtitle="Error_please_try_again_later" />
      </div>
    );
  }

  logMissingRmsData(
    {
      propertyId,
      channelId,
      rmsPropertyId,
      franchisorId,
      franchiseeId,
      storefrontId,
      finalStoreId,
    },
    isFlipdishChannel
  );

  return (
    <MicroFrontend
      partialIndexUrl={partialIndexUrl}
      enableManifestFile={true}
      microFrontendWindowName="property-management"
      language={languageCode}
      portalBaseUrl={baseUrl}
      otherProps={{
        'data-fd': 'collection-settings-micro-frontend',
        'data-property-id': propertyId,
        'data-country-code': country,
        'data-entity-type': entityType,
        'data-channel-id': channelId,
        'data-channel-type': type,
        'data-franchisor-id': franchisorId,
        'data-franchisee-id': franchiseeId,
        'data-storefront-id': storefrontId,
        'data-rms-property-id': rmsPropertyId,
        'data-store-id': finalStoreId,
      }}
    />
  );
};

type MappedState = ReturnType<typeof mapStateToProps>;
const mapStateToProps = (state: AppState) => ({
  currentApp: state.currentApp,
  languageCode: getActiveLanguage(state.locale),
});

export default connect(mapStateToProps)(PropertyManagement);
